import React from 'react'
import _ from 'lodash'
import classNames from 'classnames'
import { Icon } from '@blueprintjs/core'
import { IconNames } from '@blueprintjs/icons'

import { IBaseProps } from 'browser/components/atomic-elements/atoms/base-props'
import { ImageEditorModal } from '../../organisms/image-editor-modal'
import { Entity } from 'shared-libs/models/entity'
import { IImageLoaderProps, ImageLoader } from '../image-loader/image-loader'
import { IImageEditorCarouselProps } from '../../organisms/image-editor-carousel/image-editor-carousel'
import './_attachment-viewer.scss'

/**
 * @uiComponent
 */
interface IAttachmentViewerProps extends IBaseProps {
  entity?: Entity
  value: any
  frames?: any
  thumbnailProps?: Partial<IImageLoaderProps>
  carouselProps?: Partial<IImageEditorCarouselProps>
}

interface IAttachmentViewerState {
  carouselIndex: number
  maxImageHeight: number | null
}

export class AttachmentViewer extends React.PureComponent<IAttachmentViewerProps, IAttachmentViewerState> {
  private containerRef = React.createRef<HTMLDivElement>()
  private resizeObserver: ResizeObserver | null = null
  private heightGeneration = 0

  constructor(props: IAttachmentViewerProps) {
    super(props)
    this.state = {
      carouselIndex: 0,
      maxImageHeight: null,
    }
  }

  public componentDidMount() {
    this.computeMaxHeight()
    if (this.containerRef.current) {
      this.resizeObserver = new ResizeObserver(() => this.computeMaxHeight())
      this.resizeObserver.observe(this.containerRef.current)
    }
  }

  public componentWillUnmount() {
    this.resizeObserver?.disconnect()
    this.computeMaxHeight.cancel()
  }

  public componentDidUpdate(prevProps: IAttachmentViewerProps) {
    if (this.props.entity !== prevProps.entity) {
      this.setState({ carouselIndex: 0 })
    }
    if (this.props.value !== prevProps.value) {
      this.computeMaxHeight()
    }
  }

  private computeMaxHeight = _.debounce(() => {

    const containerWidth = this.containerRef.current?.offsetWidth
    if (!containerWidth) return
    const images = this.getImages()
    if (!images.length) return

    const generation = ++this.heightGeneration

    const promises = images.map((image) =>
      new Promise<number>((resolve) => {
        const img = new Image()
        img.onload = () => {
          const aspectRatio = img.naturalWidth / img.naturalHeight
          resolve(containerWidth / aspectRatio)
        }
        img.onerror = () => resolve(0)
        img.src = image.uri
      })
    )

    Promise.all(promises).then((heights) => {
      if (generation !== this.heightGeneration) return
      const maxHeight = Math.max(...heights)
      if (maxHeight > 0) {
        this.setState({ maxImageHeight: maxHeight })
      }
    })
  }, 100)

  public render() {
    const { thumbnailProps } = this.props
    const images = this.getImages()
    if (!images?.length) {
      return null
    }
    const total = images.length
    const carouselIndex = Math.min(this.state.carouselIndex, total - 1)
    const { maxImageHeight } = this.state
    return (
      <div
        ref={this.containerRef}
        className='c-attachmentViewer'
        style={maxImageHeight != null ? { height: maxImageHeight } : undefined}
      >
        <ImageLoader
          {...thumbnailProps}
          src={images[carouselIndex].uri}
          className={classNames('c-attachmentViewer-image', thumbnailProps?.className)}
          onClick={() => this.handleThumbnailClick(carouselIndex)}
        />
        {total > 1 && (
          <>
            {carouselIndex > 0 && (
              <button className='c-attachmentViewer-nav c-attachmentViewer-nav--left' onClick={this.handlePrev}>
                <Icon icon={IconNames.CHEVRON_LEFT} iconSize={24} />
              </button>
            )}
            {carouselIndex < total - 1 && (
              <button className='c-attachmentViewer-nav c-attachmentViewer-nav--right' onClick={this.handleNext}>
                <Icon icon={IconNames.CHEVRON_RIGHT} iconSize={24} />
              </button>
            )}
            <div className='c-attachmentViewer-indicator'>
              {carouselIndex + 1} of {total}
            </div>
          </>
        )}
      </div>
    )
  }

  private handlePrev = () => {
    this.setState(({ carouselIndex }) => ({ carouselIndex: Math.max(0, carouselIndex - 1) }))
  }

  private handleNext = () => {
    const total = this.getImages().length
    this.setState(({ carouselIndex }) => ({ carouselIndex: Math.min(total - 1, carouselIndex + 1) }))
  }

  private handleThumbnailClick = (index: number = 0) => {
    const { entity, frames, carouselProps } = this.props
    const schema = frames.getContext('dataSchema')
    const imagesPath = frames.getContext('valuePath').join('.')
    ImageEditorModal.open({
      ...carouselProps,
      entity,
      schema,
      imagesPath,
      index,
      onClose: () => this.forceUpdate(),
    })
  }

  public getImages() {
    const images: any[] = []
    const files = _.get(this.props.value, 'files', [])
    _.forEach(files, (file) => {
      if (file.type === 'image') {
        images.push(file)
      } else if (file.type === 'pdf') {
        Array.prototype.push.apply(images, file.preview)
      }
    })
    return images
  }
}
